"use client"

import type React from "react"
import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { emailService } from "@/services/api"
import type { ContactInfoEmailDTO } from "@/types/api"

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle")

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear status when user starts typing
    if (submitStatus !== "idle") {
      setSubmitStatus("idle")
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus("idle")

    try {
      const emailData: ContactInfoEmailDTO = {
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        message: formData.message,
      }

      await emailService.sendContactInfo(emailData)

      setSubmitStatus("success")
      setFormData({ name: "", email: "", phone: "", message: "" }) // Clear form
    } catch (error) {
      console.error("Error sending contact form:", error)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contact" className="w-full py-16 md:py-24 lg:py-32 bg-white">
      <div className="container px-4 md:px-6 max-w-2xl mx-auto">
        <div className="space-y-8 p-6 md:p-8 bg-gray-50 rounded-xl shadow-lg">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-raime-dark-blue">Envíanos un Mensaje</h2>
            <p className="text-muted-foreground text-lg">
              ¿Tienes preguntas o necesitas ayuda para encontrar tu habitación? Rellena el formulario y te
              contactaremos.
            </p>
          </div>

          {submitStatus === "success" && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-center">
                ¡Gracias por tu mensaje! Nos pondremos en contacto contigo pronto.
              </p>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-center">
                Hubo un error al enviar tu mensaje. Por favor, inténtalo de nuevo.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              type="text"
              name="name"
              placeholder="Nombre Completo"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="h-12 text-base border-gray-300 focus:border-raime-golden-brown focus:ring-raime-golden-brown"
            />
            <Input
              type="email"
              name="email"
              placeholder="Correo Electrónico"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="h-12 text-base border-gray-300 focus:border-raime-golden-brown focus:ring-raime-golden-brown"
            />
            <Input
              type="tel"
              name="phone"
              placeholder="Número de Teléfono (Opcional)"
              value={formData.phone}
              onChange={handleChange}
              disabled={isSubmitting}
              className="h-12 text-base border-gray-300 focus:border-raime-golden-brown focus:ring-raime-golden-brown"
            />
            <Textarea
              name="message"
              placeholder="Tu Mensaje..."
              value={formData.message}
              onChange={handleChange}
              rows={6}
              required
              disabled={isSubmitting}
              className="text-base border-gray-300 focus:border-raime-golden-brown focus:ring-raime-golden-brown"
            />
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-raime-golden-brown hover:bg-raime-golden-brown/90 text-white text-lg py-7 rounded-lg shadow-md transition-all duration-300 disabled:opacity-50"
            >
              {isSubmitting ? "Enviando..." : "Enviar Mensaje"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  )
}
